import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { withRouter, Switch as RouterSwitch } from 'react-router-dom'
import { Alert, Button, Col, Divider, Row, Space, Switch, Tooltip, Typography } from 'antd'
import PageLoader from '../../../components/Layouts/PageLoader'
import PrivateRoute from '../../../hocs/PrivateRoute'
import PilotReport from './PilotReport'
import { SWITCHES, documentSwitchDisabled, switchPayload } from './utils'
import {
  getRequest,
  patchRequest,
  successNotification,
  errorNotification,
  dutyAssistantConfigUrl
} from '../../../config'

const { Text } = Typography

// The assistant's intake switches (spec: duty-assistant-intake-ux, w-admin). Each change in that spec
// ships behind one of these, so the owner can turn any of it off without a deploy. Saved together
// with one button — like the Payments page — so a half-made change is never live.
const AssistantSettings = () => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(null)
  const [config, setConfig] = useState(null)
  const [monthlyCap, setMonthlyCap] = useState(null)

  const load = async () => {
    const res = await getRequest(dutyAssistantConfigUrl, 'assistant settings', setLoading)
    if (res.success) {
      setConfig(res.data)
      setSaved(res.data)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line
  }, [])

  const set = (key, value) => setConfig(c => ({ ...c, [key]: value }))

  const dirty =
    config && saved && SWITCHES.some(({ key }) => Boolean(config[key]) !== Boolean(saved[key]))

  const handleSave = async () => {
    setSaving(true)
    const res = await patchRequest(
      dutyAssistantConfigUrl,
      switchPayload(config),
      'assistant settings',
      () => {}
    )
    setSaving(false)
    if (res.success) {
      setConfig(res.data)
      setSaved(res.data)
      successNotification('Assistant settings saved')
    } else {
      const sentence =
        res.error?.response?.data?.error || res.error?.response?.data?.detail
      errorNotification(sentence || 'Could not save the assistant settings, retry.')
    }
  }

  return (
    <PageLoader className="container" loading={loading}>
      <div className="card mb-5rem">
        <div className="card-body">
          <h3 style={{ marginBottom: 8 }}>Assistant settings</h3>
          <p style={{ color: '#888', marginBottom: 16 }}>
            Every change to how the WhatsApp assistant collects a car can be switched off here,
            without a deploy. Off always means exactly how it worked before.
          </p>

          {config && (
            <>
              {saved?.imageReadingEnabled && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="The picture-reading pilot is on."
                  description={`Each picture costs up to about $0.004 to read${
                    monthlyCap ? `; the monthly cap is ${monthlyCap.toLocaleString()} pictures` : ''
                  }. At the cap, customers are asked to type instead.`}
                />
              )}

              {SWITCHES.map(({ key, label, help, group }, index) => {
                const locked = key === 'documentReadingEnabled' && documentSwitchDisabled(config)
                // A heading the first time a group appears, so the service switches read as one
                // decision rather than three more toggles on the end of a list.
                const heading = group && group !== SWITCHES[index - 1]?.group ? group : null
                const control = (
                  <Switch
                    checked={Boolean(config[key]) && !locked}
                    disabled={locked}
                    onChange={v => set(key, v)}
                  />
                )
                return (
                  <React.Fragment key={key}>
                  {heading && <Divider orientation="left">{heading}</Divider>}
                  <Row gutter={16} style={{ marginBottom: 16 }} wrap>
                    <Col flex="56px">
                      {locked ? (
                        <Tooltip title="Turn on Read pictures first">{control}</Tooltip>
                      ) : (
                        control
                      )}
                    </Col>
                    <Col flex="1 1 280px">
                      <Text strong>{label}</Text>
                      <div>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          {help}
                        </Text>
                      </div>
                    </Col>
                  </Row>
                  </React.Fragment>
                )
              })}

              <Space>
                <Button type="primary" loading={saving} onClick={handleSave} disabled={!dirty}>
                  Save
                </Button>
                {dirty && <Text type="warning">Unsaved changes</Text>}
              </Space>
            </>
          )}
        </div>
      </div>

      <PilotReport onLoaded={report => setMonthlyCap(report?.images?.monthlyCap ?? null)} />
    </PageLoader>
  )
}

const AssistantSettingsRoutes = props => (
  <RouterSwitch>
    <PrivateRoute exact path={props.match.path} component={AssistantSettings} />
  </RouterSwitch>
)

AssistantSettingsRoutes.propTypes = {
  match: PropTypes.object
}

export default withRouter(AssistantSettingsRoutes)
